import firebase from 'firebase';
import { Config } from '../config';

const API_BASE_URL = Config.Env.ApiBaseUrl;

export interface SubscriptionPlan {
  id: string;
  name: string;
  priceId: string;
  features: string[];
  price: number;
  interval: 'month' | 'year';
}

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscriptionId?: string;
  status?: string;
  currentPeriodEnd?: string;
  plan?: {
    id: string;
    planId: string; // Stripe product ID (e.g., prod_Sb1ZARse3KUtf2)
    name: string;
    priceId: string;
    amount: number;
    interval: string;
  };
  cancelAtPeriodEnd?: boolean;
  trialEnd?: string;
  isInTrialPeriod?: boolean;
  trialDaysRemaining?: number;
  trialPeriodDays?: number;
  accountCreatedAt?: string;
  isLifetimeDeal?: boolean;
  lifetimeDealPlan?: string;
  lifetimeDealProductId?: string;
}

// Plan IDs for different subscription tiers - environment specific
// Staging uses live Stripe keys, so it should use production product IDs
const isProduction = window.location.hostname === 'app.thedefinedcrm.com' ||
                     window.location.hostname === 'thedefinedcrm.com' ||
                     window.location.hostname.includes('staging');

export const PLAN_IDS = isProduction ? {
  // Production Stripe product IDs
  STARTER: 'prod_Sc5jLJzC8CL5bE',
  PROFESSIONAL: 'prod_Sc5jjZjCUOSXA4',
  ENTERPRISE: 'prod_Sc5jI5kfele13V',
  DEFINED: 'prod_SuAmzz7WBmSkSP',
  WORKFORCE_MAX: 'prod_TBEJQ7SfkQlsn0'
} as const : {
  // Sandbox/Development Stripe product IDs
  STARTER: 'prod_Sb15sfneT6GeRI',
  PROFESSIONAL: 'prod_Sb1ZARse3KUtf2',
  ENTERPRISE: 'prod_Sb1cLV1OPFavIu',
  DEFINED: 'prod_Su7GW2y3RAzNEb',
  WORKFORCE_MAX: 'prod_TBEdH3Ga5MIBWu'
} as const;

export type PlanTier = keyof typeof PLAN_IDS;

class SubscriptionService {
  private static instance: SubscriptionService;

  private constructor() {}

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Get the current user's subscription status
   * Requires companyId to be passed in (from AuthContext)
   */
  async getCurrentSubscription(companyId?: string): Promise<SubscriptionStatus> {
    try {
      if (!companyId) {
        // If no companyId provided, try to get from user data
        const user = firebase.auth().currentUser;
        if (!user) {
          throw new Error('User not authenticated');
        }

        const token = await user.getIdToken();
        const userResponse = await fetch(`${API_BASE_URL}/auth/users/${user.uid}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await userResponse.json();
        companyId = userData.data?.companyId;
      }

      if (!companyId) {
        return {
          hasActiveSubscription: false,
          status: 'none'
        };
      }

      // Fetch subscription through backend API
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/subscription`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            hasActiveSubscription: false,
            status: 'none'
          };
        }
        throw new Error(`Failed to fetch subscription: ${response.statusText}`);
      }

      const responseData = await response.json();
      const data = responseData.data || responseData; // Handle both wrapped and unwrapped responses
      
      console.log('Subscription check response:', data);

      // Check if this is a lifetime deal FIRST - lifetime deals are not in trial
      const isLifetimeDeal = data.lifetimeDeal === true;
      const lifetimeDealProductId = data.lifetimeDealProductId;

      // Check if user has an active paid subscription (not trial)
      const hasActiveSubscription = data.hasActiveSubscription === true &&
        data.subscription?.status === 'active';

      // Check if account is in trial period (first 14 days)
      // Skip trial check if user has a lifetime deal or active paid subscription
      let isInTrialPeriod = false;
      let trialDaysRemaining = 0;
      let accountCreatedAt = data.accountCreatedAt || data.createdAt; // Check both fields
      // Use company's trialPeriodDays if available, default to 14
      const trialPeriodDays = data.trialPeriodDays || 14;

      console.log('Account created at:', accountCreatedAt);

      // Only check trial period if NOT a lifetime deal and NOT an active paid subscription
      if (isLifetimeDeal) {
        console.log('=== LIFETIME DEAL - SKIPPING TRIAL CHECK ===');
        console.log('Lifetime deal product ID:', lifetimeDealProductId);
      } else if (hasActiveSubscription) {
        console.log('=== ACTIVE SUBSCRIPTION - SKIPPING TRIAL CHECK ===');
        console.log('Subscription status:', data.subscription?.status);
      } else if (accountCreatedAt) {
        const createdDate = new Date(accountCreatedAt);
        const now = new Date();
        const millisecondsSinceCreation = now.getTime() - createdDate.getTime();
        const daysSinceCreation = Math.floor(millisecondsSinceCreation / (1000 * 60 * 60 * 24));

        console.log('=== TRIAL PERIOD CHECK ===');
        console.log('Raw accountCreatedAt:', accountCreatedAt);

        console.log('Created date:', createdDate.toISOString());
        console.log('Current date:', now.toISOString());
        console.log('Milliseconds since creation:', millisecondsSinceCreation);
        console.log('Days since creation:', daysSinceCreation);
        console.log('Trial period threshold:', trialPeriodDays, 'days');

        if (daysSinceCreation <= trialPeriodDays) {
          isInTrialPeriod = true;
          trialDaysRemaining = trialPeriodDays - daysSinceCreation;
          console.log('✅ Account is in trial period with', trialDaysRemaining, 'days remaining');
        } else {
          console.log('❌ Trial period expired:', daysSinceCreation, 'days since creation (>', trialPeriodDays, 'days)');
        }
      } else {
        console.log('⚠️ No accountCreatedAt field found in response');
      }

      // Format the response to match our interface
      const result: SubscriptionStatus = {
        hasActiveSubscription: data.hasActiveSubscription || isInTrialPeriod || isLifetimeDeal,
        subscriptionId: data.subscription?.id,
        status: isLifetimeDeal ? 'lifetime' : (isInTrialPeriod ? 'trial' : (data.subscription?.status || 'none')),
        currentPeriodEnd: isLifetimeDeal ? undefined : (data.subscription?.currentPeriodEnd || data.subscription?.current_period_end),
        cancelAtPeriodEnd: data.subscription?.cancel_at_period_end,
        trialEnd: data.subscription?.trial_end,
        isInTrialPeriod,
        trialDaysRemaining,
        trialPeriodDays,
        accountCreatedAt,
        isLifetimeDeal,
        lifetimeDealProductId
      };
      
      console.log('=== SUBSCRIPTION STATUS RESULT ===');
      console.log('Has active subscription:', result.hasActiveSubscription);
      console.log('Is in trial period:', result.isInTrialPeriod);
      console.log('Status:', result.status);
      console.log('==================================');

      // Extract plan information if available
      if (isLifetimeDeal && lifetimeDealProductId) {
        // Map product ID to plan name
        const productIdToPlanName: Record<string, string> = {
          [PLAN_IDS.STARTER]: 'Starter',
          [PLAN_IDS.PROFESSIONAL]: 'Professional',
          [PLAN_IDS.ENTERPRISE]: 'Enterprise',
          [PLAN_IDS.DEFINED]: 'Defined'
        };
        const planName = productIdToPlanName[lifetimeDealProductId] || 'Lifetime';
        result.plan = {
          id: lifetimeDealProductId,
          planId: lifetimeDealProductId,
          name: `${planName} (Lifetime)`,
          priceId: data.lifetimeDealPriceId || '',
          amount: data.lifetimeDealAmount || 0,
          interval: 'lifetime'
        };
        result.lifetimeDealPlan = planName;
      } else if (data.subscription?.planName) {
        result.plan = {
          id: data.subscription.priceId || '',
          planId: data.subscription.planId || '',
          name: data.subscription.planName,
          priceId: data.subscription.priceId || '',
          amount: data.subscription.planAmount,
          interval: data.subscription.planInterval
        };
      } else if (isInTrialPeriod) {
        // During trial, user has access to all features (Defined level)
        result.plan = {
          id: 'trial',
          planId: PLAN_IDS.DEFINED,
          name: 'Trial (Full Access)',
          priceId: '',
          amount: 0,
          interval: 'month'
        };
      }

      return result;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  /**
   * Get subscription details by company ID
   */
  async getCompanySubscription(companyId: string): Promise<SubscriptionStatus> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            hasActiveSubscription: false,
            status: 'none'
          };
        }
        throw new Error(`Failed to fetch company subscription: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching company subscription:', error);
      throw error;
    }
  }

  /**
   * Get available subscription plans
   */
  async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/plans`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch plans: ${response.statusText}`);
      }

      const data = await response.json();
      return data.plans || [];
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  /**
   * Create a checkout session for subscription upgrade/downgrade
   */
  async createCheckoutSession(priceId: string, successUrl?: string, cancelUrl?: string): Promise<{ sessionUrl: string }> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          successUrl: successUrl || `${window.location.origin}/billing?success=true`,
          cancelUrl: cancelUrl || `${window.location.origin}/billing?canceled=true`
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Cancel the current subscription
   */
  async cancelSubscription(): Promise<{ success: boolean; message: string }> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{ success: boolean; message: string }> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to reactivate subscription: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  /**
   * Get subscription usage/metrics
   */
  async getSubscriptionUsage(): Promise<any> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/usage`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching subscription usage:', error);
      throw error;
    }
  }

  /**
   * Check if the current subscription is a specific plan tier
   */
  isPlan(subscription: SubscriptionStatus | undefined, tier: PlanTier): boolean {
    // During trial period, user has access to all features
    if (subscription?.isInTrialPeriod === true) {
      console.log(`isPlan(${tier}): TRUE - User is in trial period`);
      return true;
    }
    
    if (!subscription?.hasActiveSubscription || !subscription.plan?.planId) {
      console.log(`isPlan(${tier}): FALSE - No active subscription or plan`);
      return false;
    }
    
    const result = subscription.plan.planId === PLAN_IDS[tier];
    console.log(`isPlan(${tier}): ${result} - Plan ID: ${subscription.plan.planId}`);
    return result;
  }

  /**
   * Check if user is on Starter plan
   */
  isStarter(subscription: SubscriptionStatus | undefined): boolean {
    return this.isPlan(subscription, 'STARTER');
  }

  /**
   * Check if user is on Professional plan
   */
  isProfessional(subscription: SubscriptionStatus | undefined): boolean {
    return this.isPlan(subscription, 'PROFESSIONAL');
  }

  /**
   * Check if user is on Enterprise plan
   */
  isEnterprise(subscription: SubscriptionStatus | undefined): boolean {
    return this.isPlan(subscription, 'ENTERPRISE');
  }

  /**
   * Check if user is on Defined plan
   */
  isDefined(subscription: SubscriptionStatus | undefined): boolean {
    return this.isPlan(subscription, 'DEFINED');
  }

  /**
   * Check if user is on Workforce Max plan
   */
  isWorkforceMax(subscription: SubscriptionStatus | undefined): boolean {
    return this.isPlan(subscription, 'WORKFORCE_MAX');
  }

  /**
   * Check if user has at least a certain plan tier or higher
   * Tier hierarchy: STARTER < PROFESSIONAL < ENTERPRISE < DEFINED
   */
  hasMinimumPlan(subscription: SubscriptionStatus | undefined, minimumTier: PlanTier): boolean {
    // During trial period, user has access to all features (highest tier)
    if (subscription?.isInTrialPeriod === true) {
      console.log(`hasMinimumPlan(${minimumTier}): TRUE - User is in trial period`);
      return true;
    }

    if (!subscription?.hasActiveSubscription || !subscription.plan?.planId) {
      console.log(`hasMinimumPlan(${minimumTier}): FALSE - No active subscription or plan`);
      return false;
    }

    const tierHierarchy: PlanTier[] = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE', 'DEFINED'];
    const currentTierIndex = tierHierarchy.findIndex(tier => PLAN_IDS[tier] === subscription.plan?.planId);
    const minimumTierIndex = tierHierarchy.indexOf(minimumTier);

    if (currentTierIndex === -1) {
      // Unknown plan, deny access by default
      console.log(`hasMinimumPlan(${minimumTier}): FALSE - Unknown plan ID: ${subscription.plan?.planId}`);
      return false;
    }

    const result = currentTierIndex >= minimumTierIndex;
    console.log(`hasMinimumPlan(${minimumTier}): ${result} - Current tier index: ${currentTierIndex}, Minimum tier index: ${minimumTierIndex}`);
    return result;
  }

  /**
   * Get all subscriptions for a company (including add-ons)
   */
  async getAllSubscriptions(companyId: string): Promise<any[]> {
    try {
      let paymentApiUrl = 'https://dhs-payment-892b3cdec73f.herokuapp.com';

      if (window.location.hostname === 'localhost') {
        paymentApiUrl = 'http://localhost:3456';
      } else if (window.location.hostname.includes('staging')) {
        paymentApiUrl = 'https://dhs-payment-staging-2ddb584cd030.herokuapp.com';
      }

      const response = await fetch(`${paymentApiUrl}/checkCompanySubscription?companyId=${companyId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }

      const data = await response.json();
      return data.subscriptions || [];
    } catch (error) {
      console.error('Error fetching all subscriptions:', error);
      return [];
    }
  }

  /**
   * Check if company has a specific add-on by price IDs
   */
  async hasAddon(companyId: string, priceIds: string[]): Promise<boolean> {
    const subscriptions = await this.getAllSubscriptions(companyId);

    return subscriptions.some((sub: any) =>
      (sub.status === 'active' || sub.status === 'trialing') &&
      priceIds.includes(sub.priceId)
    );
  }

  /**
   * Check if company has Marketing add-on
   */
  async hasMarketingAddon(companyId: string): Promise<boolean> {
    const marketingPriceIds = ['price_1SDAD9JYP9Eist9xfX8IEExe', 'price_1SDADlJYP9Eist9xcaRDHNqZ'];
    return this.hasAddon(companyId, marketingPriceIds);
  }

  /**
   * Check if company has Employee Management add-on
   */
  async hasEmployeeManagementAddon(companyId: string): Promise<boolean> {
    const employeePriceIds = [
      'price_1SEsA1R0HTLAzD8Ea1fOfBLk', // dev monthly
      'price_1SEsAWR0HTLAzD8Eecjowvg4', // dev yearly
      'price_1SErr9JYP9Eist9xO8Gc2ZR9', // prod monthly
      'price_1SEsGCJYP9Eist9xQid379qs'  // prod yearly
    ];
    return this.hasAddon(companyId, employeePriceIds);
  }

  /**
   * Get employee limit for a company
   * Returns the number of employees allowed based on their subscription
   * - Defined plan: 20 employees included
   * - Employee Management add-on: Based on purchased quantity
   * - Other plans: null (unlimited or feature not available)
   */
  async getEmployeeLimit(companyId: string): Promise<number | null> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();

      // Get company data for employeeLimit (from Employee Management add-on)
      const companyResponse = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!companyResponse.ok) {
        throw new Error('Failed to fetch company data');
      }

      const companyData = await companyResponse.json();

      // If they have Employee Management add-on with a specific limit, use that
      if (companyData.data?.employeeLimit) {
        return companyData.data.employeeLimit;
      }

      // Check if they have Defined plan (includes 20 employees)
      const subscription = await this.getCurrentSubscription(companyId);
      if (this.isDefined(subscription)) {
        console.log('Defined plan detected - 20 employees included');
        return 20;
      }

      // No limit for other plans
      return null;
    } catch (error) {
      console.error('Error fetching employee limit:', error);
      return null;
    }
  }

  /**
   * Check if company can add more employees
   * @param companyId - The company ID
   * @param currentEmployeeCount - Current number of employees
   * @returns Object with canAdd boolean and limit info
   */
  async canAddEmployee(companyId: string, currentEmployeeCount: number): Promise<{
    canAdd: boolean;
    limit: number | null;
    remaining: number | null;
    message?: string;
  }> {
    const limit = await this.getEmployeeLimit(companyId);

    // If no limit set (unlimited or no employee management addon), allow
    if (limit === null) {
      return {
        canAdd: true,
        limit: null,
        remaining: null
      };
    }

    const remaining = limit - currentEmployeeCount;

    if (remaining <= 0) {
      return {
        canAdd: false,
        limit,
        remaining: 0,
        message: `You've reached your employee limit of ${limit}. Please upgrade your plan to add more employees.`
      };
    }

    return {
      canAdd: true,
      limit,
      remaining,
      message: remaining <= 2 ? `You have ${remaining} employee slot${remaining === 1 ? '' : 's'} remaining.` : undefined
    };
  }
}

export default SubscriptionService.getInstance();